import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";

// One implementation shared by the Accounts list and the client detail page. Deactivating is
// not just a flag flip — it optionally clears recurring schedules and closes open projects —
// so the two surfaces must not grow their own copies of this and drift apart.
//
// Everything here goes through api.* so the Supabase bearer token is attached. A bare fetch
// with credentials:"include" sends no token and 401s against requireStaffAuth; that is the
// v1.5.29 bug, where the list page reported success for a month without saving anything.

export type InactiveTarget = { id: string; account_name: string };

interface MakeClientInactiveDialogProps {
  /** The account being deactivated; null closes the dialog. */
  account: InactiveTarget | null;
  onClose: () => void;
  /** Extra query keys to invalidate on success (e.g. ["account", id] on the detail page). */
  invalidateKeys?: unknown[][];
}

export function MakeClientInactiveDialog({
  account,
  onClose,
  invalidateKeys = [],
}: MakeClientInactiveDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [pauseRecurring, setPauseRecurring] = useState(true);
  const [closeOpenProjects, setCloseOpenProjects] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset to the defaults each time the dialog is opened for an account, so a previous
  // session's checkbox state can't silently carry into the next offboarding.
  useEffect(() => {
    if (account) {
      setPauseRecurring(true);
      setCloseOpenProjects(false);
    }
  }, [account?.id]);

  const confirm = async () => {
    if (!account || saving) return;
    setSaving(true);
    try {
      await api.updateAccount(account.id, { is_active: false });
      if (pauseRecurring) await api.pauseRecurringProjects(account.id);
      if (closeOpenProjects) await api.closeOpenProjects(account.id);
      toast({ title: `${account.account_name} marked inactive` });
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to update client",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
      // Refetch on BOTH paths, not just success. These are three sequential writes, so a
      // failure partway through can still leave the flag flipped — invalidating only on
      // success would show a stale "Active" next to an error toast. Re-reading is cheap
      // and always tells the truth.
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      for (const key of invalidateKeys) queryClient.invalidateQueries({ queryKey: key });
    }
  };

  return (
    <Dialog open={!!account} onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Make Client Inactive?
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{account?.account_name}</span> will be hidden from the active client list. Open projects and time entries are not affected.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={pauseRecurring}
              onCheckedChange={(v) => setPauseRecurring(!!v)}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">Also pause recurring projects</span>
              <span className="block text-muted-foreground text-xs mt-0.5">
                Clears the recurrence schedule on any open recurring projects for this client so they stop auto-spawning.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer mt-3">
            <Checkbox
              checked={closeOpenProjects}
              onCheckedChange={(v) => setCloseOpenProjects(!!v)}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">Also close all open projects</span>
              <span className="block text-muted-foreground text-xs mt-0.5">
                Marks every active project for this client as closed.
              </span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="destructive" onClick={confirm} disabled={saving}>
            {saving ? "Working…" : "Make Inactive"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
